import { useState, useEffect } from "react";
import { t, font, Button, Input, Avatar, Badge } from "@realtyflows/ui";
import { getFolderPermissions, grantFolderPermission, revokeFolderPermission } from "../api";

/**
 * FolderShareModal — V1 Section B
 *
 * Lets a folder owner share access with another RealtyFlows user.
 * Roles: editor (full mutation rights) or viewer (read-only).
 *
 * The owner is shown but cannot be modified (they always have owner access).
 */
export default function FolderShareModal({ folderId, onClose }) {
  const [data, setData] = useState({ owner: null, grants: [] });
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("editor");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const refresh = () => {
    setLoading(true);
    getFolderPermissions(folderId)
      .then(setData)
      .catch(() => setData({ owner: null, grants: [] }))
      .finally(() => setLoading(false));
  };
  useEffect(refresh, [folderId]);

  const onShare = async () => {
    setError("");
    if (!email.trim()) {
      setError("Enter an email address");
      return;
    }
    setBusy(true);
    try {
      await grantFolderPermission(folderId, email.trim(), role);
      setEmail("");
      refresh();
    } catch (err) {
      setError(err.message || "Could not share folder");
    } finally {
      setBusy(false);
    }
  };

  const onRevoke = async (userId) => {
    if (!window.confirm("Remove this person's access?")) return;
    try {
      await revokeFolderPermission(folderId, userId);
      refresh();
    } catch (err) {
      setError(err.message || "Could not revoke");
    }
  };

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
      zIndex: 10000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
    }}>
      <div style={{
        background: t.surface, borderRadius: 16, padding: 24,
        maxWidth: 520, width: "100%",
        boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: t.textPrimary, fontFamily: font.display }}>
            Share folder
          </div>
          <button
            onClick={onClose}
            style={{ background: "none", border: "none", cursor: "pointer", fontSize: 22, color: t.textMuted, lineHeight: 1 }}
          >×</button>
        </div>

        {/* Add new */}
        <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
          <div style={{ flex: 1 }}>
            <Input
              placeholder="email@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") onShare(); }}
            />
          </div>
          <select
            value={role}
            onChange={(e) => setRole(e.target.value)}
            style={{
              padding: "8px 10px", fontSize: 12, fontFamily: font.body,
              border: `1.5px solid ${t.border}`, borderRadius: 6,
              background: t.surfaceWarm, color: t.textPrimary, outline: "none",
            }}
          >
            <option value="editor">Editor</option>
            <option value="viewer">Viewer</option>
          </select>
          <Button variant="primary" onClick={onShare} disabled={busy}>Share</Button>
        </div>
        {error && (
          <div style={{ fontSize: 11, color: t.coral, marginBottom: 10, fontFamily: font.body }}>{error}</div>
        )}
        <div style={{ fontSize: 11, color: t.textMuted, marginBottom: 16, fontFamily: font.body }}>
          Editors can add forms, upload documents, and send for signing. Viewers can only read.
        </div>

        {/* People list */}
        {loading ? (
          <div style={{ fontSize: 12, color: t.textMuted, fontFamily: font.body }}>Loading...</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {data.owner && <PersonRow person={data.owner} ownerLabel />}
            {data.grants.map((g) => (
              <PersonRow
                key={g.user_id}
                person={g}
                onRevoke={() => onRevoke(g.user_id)}
              />
            ))}
            {data.grants.length === 0 && (
              <div style={{ fontSize: 12, color: t.textMuted, fontStyle: "italic", padding: "10px 0", fontFamily: font.body }}>
                Not shared with anyone yet.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function PersonRow({ person, onRevoke, ownerLabel }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 10,
      padding: 10, background: t.surfaceWarm,
      border: `1px solid ${t.border}`, borderRadius: 8,
    }}>
      <Avatar src={person.photo_url} name={person.name} size={32} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: t.textPrimary, fontFamily: font.body, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {person.name || person.email}
        </div>
        <div style={{ fontSize: 11, color: t.textMuted, fontFamily: font.body }}>
          {person.email}
        </div>
      </div>
      <Badge color={person.role === "owner" ? t.emerald : person.role === "editor" ? t.sky : t.textMuted}>
        {ownerLabel ? "Owner" : person.role.charAt(0).toUpperCase() + person.role.slice(1)}
      </Badge>
      {!ownerLabel && onRevoke && (
        <button
          onClick={onRevoke}
          aria-label="Revoke access"
          style={{ background: "transparent", border: "none", cursor: "pointer", color: t.coral, fontSize: 16, fontWeight: 700 }}
        >
          ×
        </button>
      )}
    </div>
  );
}
